/**
 * Top-corner game chrome: Memory + RAM (left), Menu + Health (right).
 * Hidden through the first boot lines, then fades in when "Check your memory." appears.
 */

import { GAME_CONFIG } from "../config/gameConfig.js";
import { getRamStickCount, MAX_RAM_STICKS } from "../systems/memory.js";
import { isMemoryTutorialLocked, isMemoryUIOpen } from "./memoryUI.js";

export const GAME_HUD_Z = 9750;
const RAM_HUD_ID = "game-ram-hud";

/** Shared layout — keep lighting cutouts in sync via getGameHudCutouts(). */
export const GAME_HUD_LAYOUT = {
    margin: 6,
    gap: 4,
    hintW: {
        memory: 134,
        menu: 108,
    },
    hintH: 28,
    stackW: {
        ram: 108,
        health: 108,
    },
    stackH: 28,
    radius: 4,
    healthPad: 5,
    healthFillH: 10,
};

const MEMORY_HINT_BG = [12, 16, 22];
const MEMORY_HINT_TEXT = [100, 150, 180];
const MEMORY_HINT_FLASH_BG = [235, 200, 40];
const MEMORY_HINT_FLASH_TEXT = [30, 24, 6];
const MEMORY_HINT_FLASH_HZ = 2;
const HEALTH_FILL = [51, 255, 140];
const HEALTH_TRACK = [12, 16, 22];

const FADE_DURATION = 0.55;
const PANEL_OPACITY = 0.9;

let revealRequested = false;
let fade = 0;
let nodes = null;

function layoutRects(w = typeof width === "function" ? width() : 0) {
    const L = GAME_HUD_LAYOUT;
    const leftX = L.margin;
    const rightX = w - L.margin;
    const row1Y = L.margin;
    const row2Y = L.margin + L.hintH + L.gap;

    return {
        memory: { x: leftX, y: row1Y, w: L.hintW.memory, h: L.hintH },
        ram: { x: leftX, y: row2Y, w: L.stackW.ram, h: L.stackH },
        menu: { x: rightX - L.hintW.menu, y: row1Y, w: L.hintW.menu, h: L.hintH },
        health: { x: rightX - L.stackW.health, y: row2Y, w: L.stackW.health, h: L.stackH },
    };
}

function removeRamDom() {
    document.getElementById(RAM_HUD_ID)?.remove();
}

function setRamDomOpacity(opacity) {
    const hud = document.getElementById(RAM_HUD_ID);
    if (hud) hud.style.opacity = String(opacity);
}

function renderRamHudCounter() {
    const count = getRamStickCount();
    const rects = layoutRects();
    let hud = document.getElementById(RAM_HUD_ID);
    if (!hud) {
        hud = document.createElement("div");
        hud.id = RAM_HUD_ID;
        Object.assign(hud.style, {
            position: "fixed",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxSizing: "border-box",
            padding: "0 8px",
            background: "rgba(12, 16, 22, 0.9)",
            borderRadius: `${GAME_HUD_LAYOUT.radius}px`,
            pointerEvents: "none",
            zIndex: "2147483000",
            fontFamily: "ShareTechMono, monospace",
            fontSize: "12px",
            color: "rgb(150, 180, 205)",
            opacity: "0",
        });

        const label = document.createElement("span");
        label.textContent = "RAM";
        hud.appendChild(label);

        const sticks = document.createElement("div");
        sticks.dataset.role = "sticks";
        Object.assign(sticks.style, {
            display: "flex",
            alignItems: "center",
            gap: "4px",
        });
        hud.appendChild(sticks);
        document.body.appendChild(hud);
    }

    Object.assign(hud.style, {
        left: `${rects.ram.x}px`,
        top: `${rects.ram.y}px`,
        width: `${rects.ram.w}px`,
        height: `${rects.ram.h}px`,
    });

    const sticks = hud.querySelector("[data-role='sticks']");
    sticks.replaceChildren();

    for (let i = 0; i < MAX_RAM_STICKS; i++) {
        const unlocked = i < count;
        const img = document.createElement("img");
        img.src = "sprites/ram stick/ram.png";
        img.alt = "";
        Object.assign(img.style, {
            width: "14px",
            height: "14px",
            imageRendering: "pixelated",
            opacity: unlocked ? "1" : "0.45",
            filter: unlocked ? "none" : "grayscale(1) brightness(0.55)",
        });
        sticks.appendChild(img);
    }
}

function applyFade(opacity) {
    if (!nodes) return;
    const panelA = opacity * PANEL_OPACITY;
    nodes.memBg.opacity = panelA;
    nodes.escBg.opacity = panelA;
    nodes.healthBg.opacity = panelA;
    nodes.healthFill.opacity = opacity;
    nodes.memText.opacity = opacity;
    nodes.escText.opacity = opacity;
    setRamDomOpacity(opacity);
}

function readPlayerHp() {
    const players = get("player");
    const p = players?.[0];
    const max = GAME_CONFIG.player?.maxHp ?? 100;
    if (!p) return { hp: max, max };
    const hp = typeof p.hp === "number" ? p.hp : max;
    return { hp: Math.max(0, Math.round(hp)), max };
}

function healthFillWidth(ratio, trackW) {
    const pad = GAME_HUD_LAYOUT.healthPad;
    const inner = Math.max(0, trackW - pad * 2);
    return Math.max(0, Math.round(inner * Math.max(0, Math.min(1, ratio))));
}

/**
 * Lighting punches these rects so multiply overlay doesn't darken the chrome.
 * Empty while HUD is fully hidden (boot dialogue).
 */
export function getGameHudCutouts() {
    if (fade <= 0.02) return [];
    const rects = layoutRects();
    const r = GAME_HUD_LAYOUT.radius;
    return [
        { ...rects.memory, radius: r },
        { ...rects.ram, radius: r },
        { ...rects.menu, radius: r },
        { ...rects.health, radius: r },
    ];
}

/** Call when the third boot line ("Check your memory.") starts showing. */
export function revealGameHud() {
    revealRequested = true;
}

export function resetGameHud() {
    revealRequested = false;
    fade = 0;
    nodes = null;
    removeRamDom();
}

export function addGameHud() {
    resetGameHud();
    const rects = layoutRects();
    const L = GAME_HUD_LAYOUT;
    const fillH = L.healthFillH;
    const fillY = rects.health.y + Math.round((rects.health.h - fillH) / 2);

    const memBg = add([
        rect(rects.memory.w, rects.memory.h, { radius: L.radius }),
        pos(rects.memory.x, rects.memory.y),
        color(...MEMORY_HINT_BG),
        opacity(0),
        fixed(),
        z(GAME_HUD_Z),
        "game_hud",
    ]);

    const memText = add([
        text("Memory (SPACE)", { font: "ShareTechMono", size: 13 }),
        pos(rects.memory.x + 8, rects.memory.y + 7),
        color(...MEMORY_HINT_TEXT),
        opacity(0),
        fixed(),
        z(GAME_HUD_Z + 1),
        "game_hud",
    ]);

    const escBg = add([
        rect(rects.menu.w, rects.menu.h, { radius: L.radius }),
        pos(rects.menu.x + rects.menu.w, rects.menu.y),
        anchor("topright"),
        color(12, 16, 22),
        opacity(0),
        fixed(),
        z(GAME_HUD_Z),
        "game_hud",
    ]);

    const escText = add([
        text("Menu (ESC)", { font: "ShareTechMono", size: 13 }),
        pos(rects.menu.x + rects.menu.w - 8, rects.menu.y + 7),
        anchor("topright"),
        color(100, 150, 180),
        opacity(0),
        fixed(),
        z(GAME_HUD_Z + 1),
        "game_hud",
    ]);

    const healthBg = add([
        rect(rects.health.w, rects.health.h, { radius: L.radius }),
        pos(rects.health.x + rects.health.w, rects.health.y),
        anchor("topright"),
        color(...HEALTH_TRACK),
        opacity(0),
        fixed(),
        z(GAME_HUD_Z),
        "game_hud",
    ]);

    const { hp: startHp, max: startMax } = readPlayerHp();
    const healthFill = add([
        rect(healthFillWidth(startHp / startMax, rects.health.w), fillH, { radius: 2 }),
        pos(rects.health.x + L.healthPad, fillY),
        color(...HEALTH_FILL),
        opacity(0),
        fixed(),
        z(GAME_HUD_Z + 1),
        "game_hud",
    ]);

    nodes = { memBg, memText, escBg, escText, healthBg, healthFill };

    let renderedRamCount = -1;
    let memHintFlashOn = false;
    let lastFillW = -1;

    renderRamHudCounter();
    applyFade(0);

    onUpdate(() => {
        const next = layoutRects(width());

        memBg.pos = vec2(next.memory.x, next.memory.y);
        memText.pos = vec2(next.memory.x + 8, next.memory.y + 7);
        escBg.pos = vec2(next.menu.x + next.menu.w, next.menu.y);
        escText.pos = vec2(next.menu.x + next.menu.w - 8, next.menu.y + 7);
        healthBg.pos = vec2(next.health.x + next.health.w, next.health.y);

        const nextFillY = next.health.y + Math.round((next.health.h - L.healthFillH) / 2);
        healthFill.pos = vec2(next.health.x + L.healthPad, nextFillY);

        if (revealRequested && fade < 1) {
            fade = Math.min(1, fade + dt() / FADE_DURATION);
            applyFade(fade);
        }

        const wantsMemoryHint = isMemoryTutorialLocked() && !isMemoryUIOpen() && fade > 0.2;
        const flashPhase = wantsMemoryHint && Math.floor(time() * MEMORY_HINT_FLASH_HZ) % 2 === 0;
        if (flashPhase !== memHintFlashOn) {
            memHintFlashOn = flashPhase;
            const bg = flashPhase ? MEMORY_HINT_FLASH_BG : MEMORY_HINT_BG;
            const txt = flashPhase ? MEMORY_HINT_FLASH_TEXT : MEMORY_HINT_TEXT;
            memBg.color = rgb(...bg);
            memText.color = rgb(...txt);
        }

        const ramCount = getRamStickCount();
        if (ramCount !== renderedRamCount) {
            renderedRamCount = ramCount;
            renderRamHudCounter();
            setRamDomOpacity(fade);
        } else {
            const hud = document.getElementById(RAM_HUD_ID);
            if (hud) {
                hud.style.left = `${next.ram.x}px`;
                hud.style.top = `${next.ram.y}px`;
            }
        }

        const { hp, max } = readPlayerHp();
        const fillW = healthFillWidth(hp / max, next.health.w);
        if (fillW !== lastFillW) {
            lastFillW = fillW;
            healthFill.width = fillW;
        }
    });
}
